import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarClock, RefreshCw, Clock, Calendar, Trash2, Check, X, Pencil, Users } from "lucide-react";
import ModelsHeader from "../../components/Settings/ModelsHeader";
import ScheduledJobsIcon from "../../components/Sidebar/ScheduledJobsIcon";
import Loader from "../../components/Loader/Loader";
import SchedulerCard from "../../components/Scheduler/SchedulerCard";
import EmptyState from "../../components/shared/EmptyState";
import { useListSchedulerJobs, useUpdateSchedulerJob, useDeleteSchedulerJob, useListAdminSchedulerJobs, useDeleteAdminSchedulerJob } from "../../api/queryHooks";
import { useTheme } from "../../context/ThemeProvider";
import UIStore from "../../store/uiStore";
import useFeedback from "../../hooks/useFeedback";

// ─── Constants ────────────────────────────────────────────────────────────────

const SCHEDULE_LABELS = {
  EVERY_15MIN:    { label: "Every 15 min",   icon: RefreshCw, color: "text-ink-violet-600 bg-fill-hex-ede9fe dark:bg-fill-violet-600/20 dark:text-ink-hex-c4b5fd" },
  EVERY_30MIN:    { label: "Every 30 min",   icon: RefreshCw, color: "text-ink-violet-600 bg-fill-hex-ede9fe dark:bg-fill-violet-600/20 dark:text-ink-hex-c4b5fd" },
  HOURLY:         { label: "Every hour",     icon: Clock,     color: "text-ink-blue-600 bg-fill-blue-100 dark:bg-fill-blue-600/20 dark:text-ink-blue-300" },
  // Legacy fixed keys kept for backward compat
  DAILY_9AM:      { label: "Daily · 9 AM",   icon: Clock,     color: "text-ink-hex-0891b2 bg-fill-hex-cffafe dark:bg-fill-hex-0891b2/20 dark:text-ink-hex-67e8f9" },
  WEEKLY_MON_9AM: { label: "Mon · 9 AM",     icon: Calendar,  color: "text-ink-hex-059669 bg-fill-emerald-100 dark:bg-fill-hex-059669/20 dark:text-ink-emerald-300" },
};

const _HOUR_FMT = (h) =>
  h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`;

const _TIME_FMT = (h, m) =>
  m === 0 ? _HOUR_FMT(h) : `${_HOUR_FMT(h).replace(" ", `:${String(m).padStart(2, "0")} `)}`;

const _DOW_SHORT = { MON:"Mon", TUE:"Tue", WED:"Wed", THU:"Thu", FRI:"Fri", SAT:"Sat", SUN:"Sun" };

function parseScheduleKey(key) {
  if (!key) return null;
  if (SCHEDULE_LABELS[key]) return SCHEDULE_LABELS[key];
  // DAILY_{hour} or DAILY_{hour}_{minute}
  const daily = key.match(/^DAILY_(\d+)(?:_(\d+))?$/);
  if (daily) {
    const h = parseInt(daily[1], 10);
    const m = daily[2] ? parseInt(daily[2], 10) : 0;
    return { label: `Daily · ${_TIME_FMT(h, m)}`, icon: Clock, color: "text-ink-hex-0891b2 bg-fill-hex-cffafe dark:bg-fill-hex-0891b2/20 dark:text-ink-hex-67e8f9" };
  }
  // WEEKLY_{DOW}_{hour} or WEEKLY_{DOW}_{hour}_{minute}
  const weekly = key.match(/^WEEKLY_([A-Z]+)_(\d+)(?:_(\d+))?$/);
  if (weekly) {
    const dow = _DOW_SHORT[weekly[1]] || weekly[1];
    const h = parseInt(weekly[2], 10);
    const m = weekly[3] ? parseInt(weekly[3], 10) : 0;
    return { label: `${dow} · ${_TIME_FMT(h, m)}`, icon: Calendar, color: "text-ink-hex-059669 bg-fill-emerald-100 dark:bg-fill-hex-059669/20 dark:text-ink-emerald-300" };
  }
  return { label: key, icon: CalendarClock, color: "text-ink-gray-500 bg-fill-gray-100 dark:bg-fill-gray-700 dark:text-ink-gray-400" };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const FrequencyBadge = ({ cron_expression }) => {
  const meta = parseScheduleKey(cron_expression);
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span className={`photon-metadata-label inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const EnabledBadge = ({ enabled }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
    enabled
      ? "bg-fill-green-100 text-ink-green-800 dark:bg-fill-green-600/20 dark:text-ink-green-300"
      : "bg-fill-gray-100 text-ink-gray-500 dark:bg-fill-gray-700 dark:text-ink-gray-400"
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${enabled ? "bg-fill-green-600" : "bg-fill-gray-400"}`} />
    {enabled ? "Active" : "Paused"}
  </span>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`photon-switch relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
      disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
    } ${checked ? "bg-button-primary" : "bg-fill-gray-300 dark:bg-fill-gray-600"}`}
    role="switch"
    aria-checked={checked}
  >
    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-fill-white shadow transition-transform ${
      checked ? "translate-x-[18px]" : "translate-x-[2px]"
    }`} />
  </button>
);

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return null; }
};

// ─── Job Row ──────────────────────────────────────────────────────────────────

const JobRow = ({ job }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isEditing, setIsEditing]         = useState(false);
  const { theme } = useTheme();
  const { showFeedback } = useFeedback();

  const { mutate: updateJob, isPending: isUpdating } = useUpdateSchedulerJob({
    onError: () => showFeedback("Failed to update job. Please try again.", "error"),
  });
  const { mutate: deleteJob, isPending: isDeleting } = useDeleteSchedulerJob({
    onSuccess: () => setConfirmDelete(false),
    onError: () => showFeedback("Failed to delete job. Please try again.", "error"),
  });

  const handleToggle = (val) => {
    updateJob({ job_id: job.job_id, enabled: val });
  };

  const handleDelete = () => {
    deleteJob(job.job_id);
  };

  const lastRun = formatDate(job.last_run_at);
  const createdAt = formatDate(job.created_at);

  if (isEditing) {
    return (
      <motion.div
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="w-full"
      >
        <SchedulerCard theme={theme} job={job} onClose={() => setIsEditing(false)} />
      </motion.div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className="group flex items-start gap-4 px-5 py-4 rounded-xl border border-edge-neutral-surface dark:border-edge-dialog-border bg-surface-card dark:bg-surface-card hover:border-edge-selection-border dark:hover:border-edge-muted-control transition-colors duration-150"
    >
      {/* Icon */}
      <div className="photon-settings-icon mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-bl from-gradient-primary to-gradient-accent flex items-center justify-center">
        <CalendarClock className="w-4 h-4 text-ink-white" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Label + badges row */}
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-ink-ink dark:text-ink-white truncate max-w-[220px]">
            {job.label || "Untitled job"}
          </span>
          <FrequencyBadge cron_expression={job.cron_expression} />
          <EnabledBadge enabled={job.enabled !== false} />
        </div>

        {/* Prompt preview */}
        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 line-clamp-2 leading-5 mb-2">
          {job.prompt}
        </p>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 text-[10px] text-ink-gray-400 dark:text-ink-gray-500">
          {lastRun && (
            <span>Last run: <span className="text-ink-gray-500 dark:text-ink-gray-400">{lastRun}</span></span>
          )}
          {createdAt && (
            <span>Created: <span className="text-ink-gray-500 dark:text-ink-gray-400">{createdAt}</span></span>
          )}
          {job.spark_id && (
            <span className="font-mono">Spark: {job.spark_id.slice(0, 8)}…</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 flex-shrink-0 mt-0.5">
        {/* Enable toggle */}
        <Toggle
          checked={job.enabled !== false}
          onChange={handleToggle}
          disabled={isUpdating || isDeleting}
        />

        {/* Edit */}
        <button
          onClick={() => setIsEditing(true)}
          title="Edit job"
          className="w-7 h-7 rounded-full flex items-center justify-center text-ink-gray-400 opacity-0 group-hover:opacity-100 hover:text-ink-primary dark:hover:text-ink-accent hover:bg-fill-indigo-50 dark:hover:bg-fill-primary/20 transition-all duration-150"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>

        {/* Delete */}
        <AnimatePresence mode="wait">
          {confirmDelete ? (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex items-center gap-1"
            >
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                title="Confirm delete"
                className="w-6 h-6 rounded-full flex items-center justify-center bg-fill-red-100 dark:bg-fill-red-600/20 text-ink-red-800 dark:text-ink-red-300 hover:opacity-80 disabled:opacity-50 transition-opacity"
              >
                <Check className="w-3 h-3" />
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                title="Cancel"
                className="w-6 h-6 rounded-full flex items-center justify-center text-ink-gray-400 hover:text-ink-red-600 dark:hover:text-ink-red-300 hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </motion.div>
          ) : (
            <motion.button
              key="trash"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConfirmDelete(true)}
              title="Delete job"
              className="w-7 h-7 rounded-full flex items-center justify-center text-ink-gray-400 opacity-0 group-hover:opacity-100 hover:text-ink-red-600 dark:hover:text-ink-red-300 hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 transition-all duration-150"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ─── Admin Job Row ────────────────────────────────────────────────────────────

const AdminJobRow = ({ job }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { showFeedback } = useFeedback();
  const { mutate: deleteJob, isPending: isDeleting } = useDeleteAdminSchedulerJob({
    onSuccess: () => setConfirmDelete(false),
    onError: () => showFeedback("Failed to delete job. Please try again.", "error"),
  });

  const handleDelete = () => deleteJob(job.job_id);
  const lastRun   = formatDate(job.last_run_at);
  const createdAt = formatDate(job.created_at);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className="group flex items-start gap-4 px-5 py-4 rounded-xl border border-edge-neutral-surface dark:border-edge-dialog-border bg-surface-card dark:bg-surface-card hover:border-edge-selection-border dark:hover:border-edge-muted-control transition-colors duration-150"
    >
      <div className="photon-settings-icon mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-bl from-gradient-primary to-gradient-accent flex items-center justify-center">
        <CalendarClock className="w-4 h-4 text-ink-white" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-ink-ink dark:text-ink-white truncate max-w-[220px]">
            {job.label || "Untitled job"}
          </span>
          <FrequencyBadge cron_expression={job.cron_expression} />
          <EnabledBadge enabled={job.enabled !== false} />
        </div>
        <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 line-clamp-2 leading-5 mb-2">
          {job.prompt}
        </p>
        <div className="flex flex-wrap items-center gap-3 text-[10px] text-ink-gray-400 dark:text-ink-gray-500">
          {job.email_id && (
            <span className="inline-flex items-center gap-1">
              <Users className="w-2.5 h-2.5" />
              <span className="text-ink-gray-500 dark:text-ink-gray-400">{job.email_id}</span>
            </span>
          )}
          {lastRun && <span>Last run: <span className="text-ink-gray-500 dark:text-ink-gray-400">{lastRun}</span></span>}
          {createdAt && <span>Created: <span className="text-ink-gray-500 dark:text-ink-gray-400">{createdAt}</span></span>}
        </div>
      </div>

      <div className="flex items-center flex-shrink-0 mt-0.5">
        <AnimatePresence mode="wait">
          {confirmDelete ? (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex items-center gap-1"
            >
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                title="Confirm delete"
                className="w-6 h-6 rounded-full flex items-center justify-center bg-fill-red-100 dark:bg-fill-red-600/20 text-ink-red-800 dark:text-ink-red-300 hover:opacity-80 disabled:opacity-50 transition-opacity"
              >
                <Check className="w-3 h-3" />
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                title="Cancel"
                className="w-6 h-6 rounded-full flex items-center justify-center text-ink-gray-400 hover:text-ink-red-600 dark:hover:text-ink-red-300 hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </motion.div>
          ) : (
            <motion.button
              key="trash"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConfirmDelete(true)}
              title="Delete job"
              className="w-7 h-7 rounded-full flex items-center justify-center text-ink-gray-400 opacity-0 group-hover:opacity-100 hover:text-ink-red-600 dark:hover:text-ink-red-300 hover:bg-fill-red-100 dark:hover:bg-fill-red-600/20 transition-all duration-150"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const ScheduledJobsPage = () => {
  const { data: jobs, isLoading, error } = useListSchedulerJobs();
  const orgAvailable = UIStore((state) => state.orgAvailable);
  const isAdmin = orgAvailable === true;

  // Admin: all-org jobs (only fetched when admin)
  const {
    data: adminJobs,
    isLoading: adminLoading,
    error: adminError,
  } = useListAdminSchedulerJobs({ enabled: isAdmin });

  // 503 means the scheduler service hasn't been deployed yet — treat as empty
  const isUnavailable = error && /503/.test(error.message);
  const isRealError = error && !isUnavailable;

  const isAdminUnavailable = adminError && /503|403/.test(adminError.message);
  const isAdminRealError = adminError && !isAdminUnavailable;

  const adminTotalJobs   = adminJobs?.length ?? 0;
  const adminActiveJobs  = adminJobs?.filter((j) => j.enabled !== false).length ?? 0;
  const adminUniqueUsers = adminJobs ? new Set(adminJobs.map((j) => j.user_id)).size : 0;

  return (
    <div className="w-full h-full flex flex-col">
      <ModelsHeader title="Scheduled Jobs" icon={<ScheduledJobsIcon className="h-5 w-5 mr-2.5 flex-shrink-0" />} />

      <div className="flex-1 overflow-auto show-scrollbar">
        <div className="max-w-3xl px-6 mx-auto py-8 space-y-10">

          {/* ── My Jobs ────────────────────────────────────────────────── */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500 mb-4">
              My Jobs
            </h2>

            {jobs && jobs.length > 0 && (
              <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-4 leading-5">
                Prompts scheduled to run automatically on a recurring cadence. Toggle to pause a job or delete it entirely.
              </p>
            )}

            {isLoading && <div className="py-8"><Loader variant="dashboard" /></div>}

            {isRealError && !isLoading && (
              <p className="text-sm text-ink-red-500 dark:text-ink-red-400 py-6 text-center">
                Failed to load scheduled jobs — {error.message}
              </p>
            )}

            {!isLoading && !isRealError && (
              <>
                {jobs && jobs.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-ink-gray-500 dark:text-ink-gray-400">
                        {jobs.length} job{jobs.length !== 1 ? "s" : ""}
                      </span>
                      <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                        {jobs.filter((j) => j.enabled !== false).length} active
                      </span>
                    </div>
                    <AnimatePresence>
                      {jobs.map((job) => (
                        <JobRow key={job.id} job={job} />
                      ))}
                    </AnimatePresence>
                  </div>
                ) : (
                  <EmptyState
                    icon={<CalendarClock className="w-7 h-7 text-ink-primary dark:text-ink-accent" />}
                    title="No scheduled jobs yet"
                    description="Hover over any message in a Spark and click the clock icon to schedule it on a recurring cadence."
                  />
                )}
              </>
            )}
          </section>

          {/* ── All Jobs (admin only) ───────────────────────────────────── */}
          {isAdmin && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-gray-400 dark:text-ink-gray-500">
                  All Jobs
                </h2>
                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-fill-indigo-50 dark:bg-fill-primary/15 text-ink-primary dark:text-ink-accent">
                  Admin
                </span>
              </div>

              <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-4 leading-5">
                All scheduled jobs across your organisation. Pause or remove jobs that are causing issues.
              </p>

              {adminLoading && <div className="py-8"><Loader variant="dashboard" /></div>}

              {isAdminRealError && !adminLoading && (
                <p className="text-sm text-ink-red-500 dark:text-ink-red-400 py-6 text-center">
                  Failed to load org jobs — {adminError.message}
                </p>
              )}

              {!adminLoading && !isAdminRealError && (
                <>
                  {adminJobs && adminJobs.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-ink-gray-500 dark:text-ink-gray-400">
                          {adminTotalJobs} job{adminTotalJobs !== 1 ? "s" : ""} · {adminUniqueUsers} member{adminUniqueUsers !== 1 ? "s" : ""}
                        </span>
                        <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                          {adminActiveJobs} active
                        </span>
                      </div>
                      <AnimatePresence>
                        {adminJobs.map((job) => (
                          <AdminJobRow key={job.job_id} job={job} />
                        ))}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <EmptyState
                      icon={<CalendarClock className="w-7 h-7 text-ink-primary dark:text-ink-accent" />}
                      title="No scheduled jobs in this org"
                      description="When members schedule prompts from their Spark conversations, they'll appear here."
                    />
                  )}
                </>
              )}
            </section>
          )}

        </div>
      </div>
    </div>
  );
};

export default ScheduledJobsPage;
